import React, { useMemo } from 'react'
import { useSelector } from 'react-redux'
import PropTypes from 'prop-types'
import { FormattedMessage, useIntl } from 'react-intl'
import { min, max } from 'd3'

// Utils
import { ARM_MORTGAGE_TYPE } from '../../../constants/loan'
import { REFI_CHART_MESSAGES } from '../../../constants/refinance'

// Selectors
import { selectHasMortgageCoach } from '../../../store/selectors/customerProfile'
import { selectContactFirstName } from '../../../store/selectors/contact'

// Components
import { Text, Format, If, Unless } from '../..'
import RefiViz from '../../shared/RefiViz'
import RefiMessages from '../../shared/RefiModule/RefiByYearsInHome/RefiMessages'
import { SlideOnMount } from '../../shared/Animations'
import MortgageCoachRequestButton from './MortgageCoachRequestButton'
import RefiRequestButton from './RefiRequestButton'
import RefiDetailsVideo from './RefiDetailsVideo/RefiDetailsVideo'
import CospoIntro from './CospoIntro'

// Styles
import styles from './RefiDetailsOverlay.module.scss'

const propTypes = {
  type: PropTypes.string.isRequired,
  term: PropTypes.number.isRequired,
  years: PropTypes.number.isRequired,
  rate: PropTypes.number.isRequired,
  apr: PropTypes.number.isRequired,
  longterm: PropTypes.number,
  shortterm: PropTypes.number.isRequired,
  closing: PropTypes.number.isRequired,
  breakeven: PropTypes.number,
  potential30Year: PropTypes.number,
  disableAnimation: PropTypes.bool,
  footerId: PropTypes.string
}

const RefiDetailsBody = ({ disableAnimation, footerId, ...details }) => {
  const { type, years, datapoints } = details
  const is30Fixed = years === 30 && type === 'FIX'
  const hasMortgageCoach = useSelector(selectHasMortgageCoach)

  const contactFirstName = useSelector(selectContactFirstName)

  const intl = useIntl()
  const chartOptions = useMemo(
    () =>
      datapoints
        ? {
            verticalPaddingFactor: 0,
            min: min(datapoints ? datapoints.map(d => d.y) : 0),
            max: max(datapoints ? datapoints.map(d => d.y) : 0),
            datapoints: datapoints.map(attrs => ({
              ...attrs,
              tooltipEmphasis:
                type === ARM_MORTGAGE_TYPE && attrs.x > years
                  ? intl.formatMessage(REFI_CHART_MESSAGES.unknown)
                  : Format.currency(attrs.y),
              tooltipDescription: intl.formatMessage(REFI_CHART_MESSAGES.tooltip, {
                year: attrs.x
              })
            }))
          }
        : null,
    [datapoints, type, years, intl]
  )

  return (
    <div className={styles.body} data-qa='refinance-details-body'>
      <SlideOnMount disabled={disableAnimation}>
        <Text size='l' element='div' className={styles.headline} variant='neutralCool' variantCount='900'>
          <RefiMessages.DetailsHeadline {...details} />
        </Text>
        <div className={styles.bullets}>
          <RefiMessages.DetailsBullets {...details} />
        </div>
      </SlideOnMount>
      <SlideOnMount disabled={disableAnimation}>
        <If condition={hasMortgageCoach}>
          <MortgageCoachRequestButton {...details} />
        </If>
        <Unless condition={hasMortgageCoach}>
          <RefiRequestButton {...details} />
        </Unless>
      </SlideOnMount>
      <SlideOnMount disabled={disableAnimation}>
        <If condition={is30Fixed}>
          <RefiDetailsVideo footerId={footerId} />
        </If>
        <Unless condition={is30Fixed}>
          <CospoIntro footerId={footerId} />
        </Unless>
      </SlideOnMount>
      <SlideOnMount disabled={disableAnimation}>
        <div className={styles.specifics}>
          <div className={styles.visualization}>
            <RefiViz {...details} options={chartOptions} />
          </div>
          <div className={styles.refiCopyBlock}>
            <Text size='l' element='div' variant='neutralCool' variantCount='900' className={styles.heading}>
              <FormattedMessage id='Refinance.details.generic.title' defaultMessage='How much does refinancing cost?' />
            </Text>
            <Text size='s' variant='neutralCool' variantCount='700' className={styles.copyBlock}>
              <FormattedMessage
                id='Refinance.details.generic.part1'
                defaultMessage='{name} is happy to walk you through their specific fee structure. Your mortgage estimate depends on market rates, your current financial situation, home occupancy status, and refi goals.'
                values={{
                  name: contactFirstName
                }}
              />
            </Text>
            <Text size='s' variant='neutralCool' variantCount='700' className={styles.copyBlock}>
              <FormattedMessage
                id='Refinance.details.generic.part2'
                defaultMessage='Refinancing comes with costs, but you can sometimes roll those refi costs into your loan instead of bringing cash to the closing.'
              />
            </Text>
          </div>
        </div>
      </SlideOnMount>
    </div>
  )
}

RefiDetailsBody.propTypes = propTypes
export default RefiDetailsBody
